import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { OrganizationDependencyContainer } from '../../infrastructure/dependencies/OrganizationDependencyContainer';
import { CreateOrganizationData, UpdateOrganizationData } from '../../domain/repositories/IOrganizationRepository';
import { useAuthStore } from '@/auth/presentation/store/authStore';

const QUERY_KEY = ['organizations'] as const;

export function useOrganizations() {
  const { t } = useTranslation();
  const isSuperAdmin = useAuthStore((s) => s.user?.role === 'superadmin');
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const orgRepo = OrganizationDependencyContainer.getOrganizationRepository();
      const planRepo = OrganizationDependencyContainer.getPlanRepository();
      const [{ organizations, totals }, plans] = await Promise.all([
        orgRepo.list(),
        planRepo.list(),
      ]);
      return { organizations, totals, plans };
    },
    enabled: isSuperAdmin,
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateOrganizationData) => {
      const orgRepo = OrganizationDependencyContainer.getOrganizationRepository();
      return orgRepo.create(data);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateOrganizationData }) => {
      const orgRepo = OrganizationDependencyContainer.getOrganizationRepository();
      return orgRepo.update(id, data);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });

  return {
    organizations: query.data?.organizations ?? [],
    plans: query.data?.plans ?? [],
    totals: query.data?.totals ?? { orgs: 0, connections: 0, users: 0, tasks: 0, items: 0 },
    isLoading: query.isLoading,
    // Fallo de carga del listado: el `err.message` de axios ("Request failed with status code N")
    // es técnico y quedaba visible sin traducir. Se muestra un mensaje genérico traducido.
    error: query.error ? t('common.load_error') : null,
    createOrganization: (data: CreateOrganizationData) => createMutation.mutateAsync(data),
    updateOrganization: (id: string, data: UpdateOrganizationData) =>
      updateMutation.mutateAsync({ id, data }),
    reload: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  };
}
